import { useState, useRef, useCallback } from 'react';
import {
  LayoutType,
  LayoutOptions,
  NodeType,
  DEFAULT_LAYOUT,
} from '../types/cytoscape';
import { NodeSingular, Core } from 'cytoscape';
import { setupMultiThemeSpiralLayout } from '@/features/connect-dots/styles/networkStyles';

export const useNetworkLayout = (
  cytoscapeCoreRefObject: React.MutableRefObject<Core | null>,
  nodeSpacingFactor: number,
  selectedNode: { id: string } | null
) => {
  const [currentLayout, setCurrentLayout] =
    useState<LayoutType>(DEFAULT_LAYOUT);
  const isLayoutRunningRef = useRef<boolean>(false);
  const prevLayoutRef = useRef<string>(currentLayout);

  const getLayoutConfig = useCallback((): Record<LayoutType, LayoutOptions> => {
    const cy = cytoscapeCoreRefObject.current;
    if (!cy) {
      return {} as Record<LayoutType, LayoutOptions>;
    }

    return {
      [LayoutType.CONCENTRIC]: {
        name: 'concentric',
        animate: true,
        animationDuration: 1000,
        avoidOverlap: false,
        padding: 30,
        concentric: function (node: NodeSingular) {
          if (node.data('nodeType') === NodeType.THEME) {
            return 12;
          } else if (node.data('nodeType') === NodeType.SOCIAL_ISSUE) {
            return 8;
          } else if (node.data('nodeType') === NodeType.CROSS) {
            return 6;
          } else if (node.data('nodeType') === NodeType.CROSS_ISSUE) {
            return 4;
          }
          // 他のノードは最も外側
          return 16;
        },
        levelWidth: function () {
          return 1;
        },
        minNodeSpacing: 50, // ノード間の最小距離
        spacingFactor: Math.max(3, 1 + nodeSpacingFactor / 50),
      },
      [LayoutType.CIRCLE]: {
        name: 'circle',
        fit: true,
        animate: true,
        animationDuration: 1000,
        avoidOverlap: false,
        padding: 80,
        spacingFactor: nodeSpacingFactor,
      },
      [LayoutType.COSE]: {
        name: 'cose',
        animate: true,
        animationDuration: 1000,
        randomize: false,
      },
      [LayoutType.GRID]: {
        name: 'grid',
        avoidOverlap: true,
        fit: true,
        animate: true,
        animationDuration: 500,
        padding: 30,
        spacingFactor: Math.max(3, 1 + nodeSpacingFactor / 50),
        minNodeSpacing: 1,
      },
      [LayoutType.RANDOM]: {
        name: 'random',
        fit: true,
        animate: true,
        animationDuration: 500,
        padding: 30,
      },
      [LayoutType.BREADTHFIRST]: {
        name: 'breadthfirst',
        fit: true,
        animate: true,
        animationDuration: 1000,
        padding: 60,
        directed: true,
        avoidOverlap: true,
        roots: (nodes: NodeSingular[]) => {
          if (selectedNode) {
            const selectedCyNode = nodes.find(
              (n) => n.id() === selectedNode.id
            );
            return selectedCyNode ? [selectedCyNode] : [];
          }
          const themeNodes = nodes.filter(
            (n) => n.data('nodeType') === 'theme'
          );
          if (themeNodes.length > 0) {
            const sortedThemeNodes = [...themeNodes].sort(
              (a, b) => (b.data('weight') || 0) - (a.data('weight') || 0)
            );
            return sortedThemeNodes.slice(
              0,
              Math.min(3, sortedThemeNodes.length)
            );
          }
          const sortedNodes = [...nodes].sort(
            (a, b) => (b.data('weight') || 0) - (a.data('weight') || 0)
          );
          return sortedNodes.slice(0, Math.min(3, sortedNodes.length));
        },
        maximal: true,
        levelDistance: (nodes: NodeSingular[]) =>
          Math.min(150, Math.max(80, nodes.length / 8)),
      },
      [LayoutType.THEME_CIRCULAR]: {
        name: 'preset', // 初期レイアウトはプリセット（手動配置）
        fit: true,
        padding: 50,
        animate: false,
        ready: function () {
          // レイアウト準備完了時に実行されるカスタム関数
          // ここでテーマノードを円形に、他のノードを内側に配置
          if (cytoscapeCoreRefObject) {
            setupMultiThemeSpiralLayout(cytoscapeCoreRefObject);
          }
        },
      },
      // 追加: PRESETレイアウト
      [LayoutType.PRESET]: {
        name: 'preset',
        fit: true,
        padding: 30,
        animate: false,
      },
    };
  }, [selectedNode, nodeSpacingFactor, cytoscapeCoreRefObject]);

  // レイアウト変更と適用を統合した関数
  const changeLayout = useCallback(
    (newLayout: LayoutType) => {
      const cy = cytoscapeCoreRefObject.current;
      if (!cy || cy.destroyed()) return;

      if (currentLayout === newLayout) return;
      setCurrentLayout(newLayout);

      if (isLayoutRunningRef.current) {
        // 既存レイアウト処理中なら何かしらの対応
      }
      isLayoutRunningRef.current = true;
      cy.off('layoutstop');

      const layoutOptions = getLayoutConfig()[newLayout];

      cy.one('layoutstop', () => {
        setTimeout(() => {
          cy.fit(undefined, 60);
          cy.center();
          isLayoutRunningRef.current = false;
          prevLayoutRef.current = newLayout;
        }, 100);
      });

      const timeoutId = setTimeout(() => {
        if (isLayoutRunningRef.current) {
          isLayoutRunningRef.current = false;
        }
      }, 1000);

      try {
        cy.layout(layoutOptions).run();
      } catch {
        isLayoutRunningRef.current = false;
        clearTimeout(timeoutId);
      }
    },
    [
      currentLayout,
      cytoscapeCoreRefObject,
      getLayoutConfig,
      nodeSpacingFactor,
      isLayoutRunningRef,
      prevLayoutRef,
    ]
  );

  return {
    currentLayout,
    setCurrentLayout,
    getLayoutConfig,
    changeLayout,
  };
};
